import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LIFE_LOGO } from "@/lib/assets";
import NexWordmark from "@/components/NexWordmark";
import { useSmartScroll } from "@/lib/useSmartScroll";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Engines", to: "/engines" },
  { label: "Live Demo", to: "/demos" },
  { label: "Case Study", to: "/case-study" },
  { label: "Program", to: "/program" },
  { label: "Pricing", to: "/pricing" },
  { label: "Founder", to: "/founder" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const smart = useSmartScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile menu on route change
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || location.pathname !== "/" ? "bg-[#0B0F19]/90 border-b border-white/10 backdrop-blur-xl" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-lockup" className="flex items-center gap-4 shrink-0">
          <img src={LIFE_LOGO} alt="Learning Institute For Empowerment" className="h-11 w-11 object-contain" />
          <div className="hidden sm:flex items-center gap-4">
            <div className="w-px h-9 bg-nex-border" />
            <NexWordmark size="text-2xl" />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-nex-gold" : "text-nex-chrome hover:text-nex-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-cta-audit"
            onClick={() => smart("#friction-audit")}
            className="tick-btn-primary text-sm hidden md:inline-flex"
          >
            Start Friction Audit
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-nex-silver p-2 border border-white/10 hover:border-nex-gold transition-colors"
            aria-label="menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B0F19]/98 border-t border-white/10 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `mono-body text-sm tracking-[0.15em] uppercase ${isActive ? "text-nex-gold" : "text-nex-silver"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={() => smart("#friction-audit")}
              data-testid="mobile-cta-audit"
              className="tick-btn-primary text-sm justify-center"
            >
              Start Friction Audit
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
